import React, { useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { buildTeamMessageStatus } from "../utils/teamMessageReceiptUtils";
import TeamMessageReceiptDialog from "./TeamMessageReceiptDialog";

const TeamMessageStatus = ({ message, spaceType, isOwn, inBubble = false }) => {
  const { t } = useTranslation("team");
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  const status = useMemo(
    () =>
      buildTeamMessageStatus({
        receipts: message?.receipts,
        messageType: message?.messageType,
        spaceType,
      }),
    [message?.receipts, message?.messageType, spaceType]
  );

  if (!isOwn || message?.authorType !== "user") return null;

  if (message?.sendStatus === "sending") {
    const pendingTooltip = t("statusSending");
    return (
      <Tooltip title={pendingTooltip} placement="top">
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            ml: inBubble ? 0.15 : 0.5,
            verticalAlign: "middle",
            lineHeight: 0,
          }}
          aria-label={pendingTooltip}
        >
          <ScheduleIcon
            sx={{
              fontSize: inBubble ? 15 : 14,
              opacity: inBubble ? 0.95 : 0.9,
              color: inBubble ? "primary.contrastText" : "text.disabled",
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  if (message?.sendStatus === "error") {
    const errorTooltip = message?.errorMessage || t("statusError");
    return (
      <Tooltip title={errorTooltip} placement="top">
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            ml: inBubble ? 0.15 : 0.5,
            verticalAlign: "middle",
            lineHeight: 0,
          }}
          aria-label={errorTooltip}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: inBubble ? 15 : 14,
              color: inBubble ? "#ffcdd2" : "error.main",
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  const tooltip = (() => {
    switch (status.level) {
      case "played":
        return t("statusPlayedBy", { name: status.playedNames[0] || t("unknownAuthor") });
      case "played_all":
        return t("statusPlayedByAll");
      case "played_some":
        return t("statusPlayedByCount", {
          count: status.playedCount,
          total: status.total,
          names: status.playedNames.slice(0, 4).join(", "),
        });
      case "read":
        return t("statusReadBy", { name: status.readNames[0] || t("unknownAuthor") });
      case "read_all":
        return t("statusReadByAll");
      case "read_some":
        return t("statusReadByCount", {
          count: status.readCount,
          total: status.total,
          names: status.readNames.slice(0, 4).join(", "),
        });
      case "delivered":
        return t("statusDelivered");
      case "delivered_all":
        return t("statusDeliveredAll");
      case "delivered_some":
        return t("statusDeliveredCount", { count: status.deliveredCount, total: status.total });
      default:
        return t("statusSent");
    }
  })();

  const canOpenReceiptDialog = status.total > 0;

  const isRead = status.icon === "read";
  const iconSx = {
    fontSize: inBubble ? 15 : 14,
    opacity: inBubble ? 0.95 : 0.9,
    color: inBubble
      ? isRead
        ? "#b8dcff"
        : "primary.contrastText"
      : isRead
        ? "info.main"
        : "text.disabled",
  };

  const handleOpenReceiptDialog = (event) => {
    if (!canOpenReceiptDialog) return;
    event.preventDefault();
    event.stopPropagation();
    setReceiptDialogOpen(true);
  };

  const statusContent = (
    <Box
      component={canOpenReceiptDialog ? "button" : "span"}
      type={canOpenReceiptDialog ? "button" : undefined}
      onClick={canOpenReceiptDialog ? handleOpenReceiptDialog : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        ml: inBubble ? 0.15 : 0.5,
        verticalAlign: "middle",
        lineHeight: 0,
        border: 0,
        p: 0,
        m: 0,
        bgcolor: "transparent",
        font: "inherit",
        color: "inherit",
        cursor: canOpenReceiptDialog ? "pointer" : "default",
        borderRadius: 0.5,
        ...(canOpenReceiptDialog
          ? {
              "&:hover": { opacity: 0.85 },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: inBubble ? "primary.contrastText" : "primary.main",
                outlineOffset: 1,
              },
            }
          : {}),
      }}
      aria-label={canOpenReceiptDialog ? `${tooltip}. ${t("receiptOpenDetails")}` : tooltip}
    >
      {status.icon === "sent" ? (
        <CheckIcon sx={iconSx} />
      ) : (
        <DoneAllIcon sx={iconSx} />
      )}
      {spaceType !== "dm" && status.level.endsWith("_some") ? (
        <Typography
          component="span"
          variant="caption"
          sx={{
            ml: 0.25,
            fontSize: "0.65rem",
            opacity: 0.85,
            color: inBubble ? "primary.contrastText" : "inherit",
          }}
        >
          {status.readCount || status.playedCount || status.deliveredCount}/{status.total}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <>
      <Tooltip
        title={tooltip}
        placement="top"
        disableInteractive
        disableTouchListener={canOpenReceiptDialog}
      >
        {statusContent}
      </Tooltip>
      {canOpenReceiptDialog ? (
        <TeamMessageReceiptDialog
          open={receiptDialogOpen}
          onClose={() => setReceiptDialogOpen(false)}
          message={message}
          spaceType={spaceType}
        />
      ) : null}
    </>
  );
};

export default TeamMessageStatus;
